import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  Cloud,
  Globe,
  Maximize2,
  DollarSign,
  Shield,
  Users,
  RefreshCw,
  Mail,
  Folder,
  Video,
  FileText,
  Settings,
  Check,
  X,
  Search,
  ClipboardList,
  ArrowRightLeft,
  TrendingUp,
  Database,
  Server,
  CloudUpload
} from "lucide-react";

const CloudSolutionsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleConsultation = () => {
    navigate("/#contact");
  };

  return (
    <div className="min-h-screen" data-testid="cloud-solutions-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/services" className="hover:text-blue-600 transition-colors">Services</Link>
              <span className="mx-2">›</span>
              <span>Cloud Solutions</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              Cloud Solutions
            </h1>
            <p className="text-lg" style={{ color: "#4A5568", maxWidth: "700px", margin: "0 auto" }}>
              Work from anywhere with secure, reliable cloud services
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-20 px-5 bg-white" data-testid="hero-section">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="flex justify-center">
                <Cloud size={180} style={{ color: "#3B82F6" }} className="hidden md:block" />
                <Cloud size={120} style={{ color: "#3B82F6" }} className="md:hidden" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: "#1F2937" }}>
                  The Future of Business is in the Cloud
                </h2>
                <p className="text-lg mb-8 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>
                  Move beyond the limitations of traditional IT infrastructure. Cloud solutions give you the flexibility, scalability, and security your business needs to thrive in today's digital world.
                </p>
                <Button
                  onClick={handleConsultation}
                  data-testid="hero-cta"
                  className="text-lg font-semibold px-8 py-6 rounded-md"
                  style={{ backgroundColor: "#3B82F6" }}
                >
                  Explore Cloud Options
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="benefits-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Why Move to the Cloud?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <BenefitCard icon={Globe} title="Work From Anywhere" description="Access your files and applications from any device, anywhere in the world" color="#3B82F6" />
              <BenefitCard icon={Maximize2} title="Scalability" description="Easily add or remove users and storage as your business grows or changes" color="#2C5F2D" />
              <BenefitCard icon={DollarSign} title="Cost Savings" description="Eliminate expensive hardware purchases and reduce maintenance costs" color="#3B82F6" />
              <BenefitCard icon={Shield} title="Enterprise Security" description="Benefit from enterprise-grade security without enterprise-level costs" color="#2C5F2D" />
              <BenefitCard icon={Users} title="Better Collaboration" description="Share files and work together in real-time, even when apart" color="#3B82F6" />
              <BenefitCard icon={RefreshCw} title="Automatic Backups" description="Your data is automatically backed up and protected from disasters" color="#2C5F2D" />
            </div>
          </div>
        </section>

        {/* Our Cloud Services Section */}
        <section className="py-20 px-5 bg-white" data-testid="cloud-services-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Our Cloud Solutions
            </h2>
            
            {/* Microsoft 365 */}
            <ServiceCard 
              icon={Cloud} 
              title="Microsoft 365" 
              subtitle="Complete productivity suite in the cloud" 
              borderColor="#3B82F6"
              buttonText="Learn About Microsoft 365"
              buttonColor="#3B82F6"
              onClick={handleConsultation}
              items={["Email hosting (Exchange Online)", "Office applications (Word, Excel, PowerPoint)", "1TB OneDrive storage per user", "Microsoft Teams for collaboration", "SharePoint for document management", "Advanced security features"]}
            />

            {/* Cloud Backup */}
            <ServiceCard 
              icon={Database} 
              title="Cloud Backup & Disaster Recovery" 
              subtitle="Protect your critical business data" 
              borderColor="#2C5F2D"
              buttonText="Protect Your Data"
              buttonColor="#2C5F2D"
              onClick={handleConsultation}
              items={["Automated daily backups", "Ransomware protection", "Point-in-time recovery", "Multiple retention periods", "Encrypted storage", "Fast restoration"]}
            />

            {/* Cloud Infrastructure */}
            <ServiceCard 
              icon={Server} 
              title="Cloud Infrastructure" 
              subtitle="Scalable computing power without the hardware" 
              borderColor="#3B82F6"
              buttonText="Explore Infrastructure"
              buttonColor="#3B82F6"
              onClick={handleConsultation}
              items={["Virtual servers", "Cloud storage solutions", "Database hosting", "Application hosting", "Load balancing", "99.9% uptime guarantee"]}
            />

            {/* Cloud Migration */}
            <ServiceCard 
              icon={CloudUpload} 
              title="Cloud Migration Services" 
              subtitle="Seamless transition to the cloud" 
              borderColor="#2C5F2D"
              buttonText="Plan Your Migration"
              buttonColor="#2C5F2D"
              onClick={handleConsultation}
              items={["Current environment assessment", "Migration planning", "Data transfer", "Application migration", "Testing and validation", "User training and support"]}
              isLast
            />
          </div>
        </section>

        {/* Cloud vs On-Premise */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="comparison-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Cloud vs. Traditional On-Premise IT
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* On-Premise */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#FCA5A5" }} data-testid="on-premise">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#1F2937" }}>On-Premise IT</h3>
                <ComparisonItem text="High upfront hardware costs" isNegative />
                <ComparisonItem text="Limited scalability" isNegative />
                <ComparisonItem text="Requires physical space" isNegative />
                <ComparisonItem text="In-house maintenance required" isNegative />
                <ComparisonItem text="Difficult remote access" isNegative />
                <ComparisonItem text="Manual backup management" isNegative />
                <ComparisonItem text="Costly disaster recovery" isNegative />
              </div>
              {/* Cloud */}
              <div className="bg-white rounded-xl p-8 border-2" style={{ borderColor: "#86EFAC" }} data-testid="cloud-comparison">
                <h3 className="text-2xl font-bold mb-6" style={{ color: "#1F2937" }}>Cloud</h3>
                <ComparisonItem text="Low upfront costs, pay-as-you-go" />
                <ComparisonItem text="Instant scalability" />
                <ComparisonItem text="No physical infrastructure needed" />
                <ComparisonItem text="Provider handles maintenance" />
                <ComparisonItem text="Access from anywhere" />
                <ComparisonItem text="Automatic backups" />
                <ComparisonItem text="Built-in disaster recovery" />
              </div>
            </div>
          </div>
        </section>

        {/* Migration Process */}
        <section className="py-20 px-5 bg-white" data-testid="migration-process">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
              Our Cloud Migration Process
            </h2>
            <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
              We make moving to the cloud simple and stress-free
            </p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <MigrationStep num="1" icon={Search} title="Discovery & Assessment" color="#3B82F6" items={["Evaluate current infrastructure", "Identify cloud-ready apps", "Assess security needs", "Calculate ROI"]} />
              <MigrationStep num="2" icon={ClipboardList} title="Planning & Design" color="#2C5F2D" items={["Create migration strategy", "Design cloud architecture", "Plan timeline", "Prepare rollback"]} />
              <MigrationStep num="3" icon={FileText} title="Preparation" color="#3B82F6" items={["Set up cloud environment", "Configure security", "Create data backup", "Test connectivity"]} />
              <MigrationStep num="4" icon={ArrowRightLeft} title="Migration" color="#2C5F2D" items={["Transfer data", "Migrate applications", "Update configurations", "Monitor progress"]} />
              <MigrationStep num="5" icon={TrendingUp} title="Optimization" color="#3B82F6" items={["Fine-tune performance", "Train your team", "Ongoing support", "Continuous optimization"]} />
            </div>
          </div>
        </section>

        {/* Microsoft 365 Features */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="m365-features">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Power Your Business with Microsoft 365
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <M365FeatureCard icon={Mail} title="Email & Calendar" color="#3B82F6" items={["Professional email addresses", "50GB mailbox per user", "Shared calendars", "Mobile access"]} />
              <M365FeatureCard icon={Folder} title="File Storage & Sharing" color="#2C5F2D" items={["1TB OneDrive per user", "Share files securely", "Real-time co-authoring", "Version history"]} />
              <M365FeatureCard icon={Video} title="Communication" color="#3B82F6" items={["Microsoft Teams", "Video conferencing", "Chat and channels", "Screen sharing"]} />
              <M365FeatureCard icon={FileText} title="Office Apps" color="#2C5F2D" items={["Word, Excel, PowerPoint", "Desktop and web versions", "Mobile apps", "Always up-to-date"]} />
              <M365FeatureCard icon={Shield} title="Security" color="#3B82F6" items={["Advanced threat protection", "Data loss prevention", "Multi-factor auth", "Encrypted email"]} />
              <M365FeatureCard icon={Settings} title="Administration" color="#2C5F2D" items={["Centralized management", "User provisioning", "Usage reports", "24/7 support"]} />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-5 bg-white" data-testid="faq-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Cloud Solutions FAQ
            </h2>
            <div className="space-y-4">
              <FAQItem q="Is the cloud secure?" a="Yes. Major cloud providers invest heavily in security, often exceeding what small businesses can implement on their own. We add additional layers of protection specific to your needs." />
              <FAQItem q="What happens if the internet goes down?" a="Most cloud applications have offline modes, and we can implement backup internet connections for critical operations." />
              <FAQItem q="How long does migration take?" a="It depends on the complexity of your environment. Simple migrations can take a few days, while complex environments might take several weeks. We'll provide a detailed timeline." />
              <FAQItem q="Can we keep some systems on-premise?" a="Absolutely. Many businesses use a hybrid approach, keeping some systems on-premise while moving others to the cloud." />
              <FAQItem q="What if we don't like the cloud?" a="While rare, you can move back to on-premise systems. However, most businesses find significant benefits and cost savings with cloud solutions." />
              <FAQItem q="How much does it cost?" a="Cloud services typically follow a per-user, per-month model. We'll provide detailed pricing based on your specific needs." />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-5" style={{ background: "linear-gradient(to right, #3B82F6, #2563EB)" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Move to the Cloud?
            </h2>
            <p className="text-lg mb-10" style={{ color: "rgba(255,255,255,0.9)" }}>
              Start with a free consultation and cloud readiness assessment
            </p>
            <Button
              onClick={handleConsultation}
              data-testid="cta-btn"
              className="text-lg font-bold px-10 py-6 rounded-lg hover:bg-gray-100 transition-all"
              style={{ backgroundColor: "white", color: "#3B82F6" }}
            >
              Schedule Cloud Consultation
            </Button>
            <p className="mt-5 text-white">
              Call{" "}
              <a href="tel:7783497449" className="font-semibold hover:underline" data-testid="cta-phone">
                (778) 349-7449
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Helper Components
const BenefitCard = ({ icon: Icon, title, description, color }) => (
  <div className="bg-white rounded-xl p-8 text-center" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
    <Icon size={48} style={{ color }} className="mx-auto mb-4" />
    <h3 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{description}</p>
  </div>
);

const ServiceCard = ({ icon: Icon, title, subtitle, borderColor, buttonText, buttonColor, onClick, items, isLast = false }) => (
  <div className={`rounded-xl p-8 md:p-12 border-2 ${isLast ? '' : 'mb-12'}`} style={{ backgroundColor: "#F9FAFB", borderColor }}>
    <Icon size={56} style={{ color: borderColor }} className="mb-4" />
    <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-lg mb-6" style={{ color: "#4A5568" }}>{subtitle}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3 py-1">
          <span style={{ color: borderColor, marginTop: "6px" }}>•</span>
          <span style={{ color: "#374151" }}>{item}</span>
        </div>
      ))}
    </div>
    <Button onClick={onClick} className="font-semibold px-7 py-5" style={{ backgroundColor: buttonColor }}>
      {buttonText}
    </Button>
  </div>
);

const ComparisonItem = ({ text, isNegative = false }) => (
  <div className="flex items-start gap-3 py-2">
    {isNegative ? (
      <X size={20} style={{ color: "#EF4444", flexShrink: 0, marginTop: "2px" }} />
    ) : (
      <Check size={20} style={{ color: "#22C55E", flexShrink: 0, marginTop: "2px" }} />
    )}
    <span style={{ color: "#4A5568" }}>{text}</span>
  </div>
);

const MigrationStep = ({ num, icon: Icon, title, color, items }) => (
  <div className="rounded-lg p-6" style={{ backgroundColor: "#F9FAFB", borderTop: `4px solid ${color}` }}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold mb-4" style={{ backgroundColor: color }}>
      {num}
    </div>
    <Icon size={32} style={{ color }} className="mb-3" />
    <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>{title}</h3>
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="text-sm" style={{ color: "#4A5568" }}>• {item}</li>
      ))}
    </ul>
  </div>
);

const M365FeatureCard = ({ icon: Icon, title, color, items }) => (
  <div className="bg-white rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
    <Icon size={40} style={{ color }} className="mb-4" />
    <h3 className="text-xl font-bold mb-4" style={{ color: "#1F2937" }}>{title}</h3>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-base" style={{ color: "#4A5568" }}>• {item}</li>
      ))}
    </ul>
  </div>
);

const FAQItem = ({ q, a }) => (
  <div className="rounded-lg p-6" style={{ backgroundColor: "#F9FAFB" }}>
    <h3 className="text-lg font-bold mb-3" style={{ color: "#1F2937" }}>{q}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568" }}>{a}</p>
  </div>
);

export default CloudSolutionsPage;
